import { Link, useNavigate } from "react-router-dom"
import DashboardSideBar from "../../components/dashboardSidebar"
import Footer from "../../components/footer"
import Header from "../../components/header"
import SideBar from "../../components/sidebar"
import { useState, useEffect, useCallback } from "react"
import services from "../../services"
import Toaster from "../../components/toaster";
import constant from "../../constant";
import useIsMobile from "../../hooks/useIsMobile";
import MobileHeader from "../../components/MobileHeader";

const AddSubscription = () => {
    let navigate = useNavigate();
    const isMobileView = useIsMobile();
    const [subscriptionData, setSubscriptionData] = useState({
        startDate: '',
        endDate: '',
        totalCost: '',
        modeOfPayment: 'online',
        isActive: false
    })
    const [toaster, setToaster] = useState({ visiblity: "hide" });
    const [loading, setLoading] = useState(false)
    const [configData, setConfigData] = useState(null)
    const [configLoading, setConfigLoading] = useState(true)
    const [dateError, setDateError] = useState('')
    const [paymentData, setPaymentData] = useState(null)
    const [showPaymentFailureModal, setShowPaymentFailureModal] = useState(false)

    // Calculate days between start and end date (inclusive)
    const calculateDays = useCallback((startDate, endDate) => {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const timeDiff = end.getTime() - start.getTime()
        const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1 // +1 to include both dates
        return daysDiff > 0 ? daysDiff : 0
    }, [])

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date()
        return today.toISOString().split('T')[0]
    }

    // Get minimum end date (start date + 1 day)
    const getMinEndDate = () => {
        if (!subscriptionData.startDate) return getTodayDate()
        const start = new Date(subscriptionData.startDate)
        start.setDate(start.getDate() + 1)
        return start.toISOString().split('T')[0]
    }

    // Load Razorpay script
    useEffect(() => {
        const loadRazorpayScript = () => {
            return new Promise((resolve) => {
                // Check if script already exists
                if (document.getElementById('razorpay-checkout-script')) {
                    if (window.Razorpay) {
                        resolve(true)
                        return
                    }
                }
                
                const script = document.createElement('script')
                script.id = 'razorpay-checkout-script'
                script.src = 'https://checkout.razorpay.com/v1/checkout.js'
                script.onload = () => {
                    console.log('Razorpay script loaded successfully')
                    resolve(true)
                }
                script.onerror = () => {
                    console.error('Failed to load Razorpay script')
                    resolve(false)
                }
                document.body.appendChild(script)
            })
        }

        loadRazorpayScript()
    }, [])

    // Fetch subscription config on component mount
    useEffect(() => {
        const fetchConfig = async () => {
            setConfigLoading(true)
            try {
                const response = await services.Vendor.GET_SYSTEM_CONFIG_BY_KEY('subscription');
                setConfigData(response.data?.data || {})
            } catch (error) {
                console.log(error)
                setToaster({
                    type: "danger",
                    title: "Error Occured",
                    text: error.response?.data?.message || "Failed to fetch subscription configuration",
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 1500)
            } finally {
                setConfigLoading(false)
            }
        }
        fetchConfig()
    }, [])

    // Calculate total cost when dates change
    useEffect(() => {
        if (subscriptionData.startDate && subscriptionData.endDate && configData?.perDayCost) {
            const days = calculateDays(subscriptionData.startDate, subscriptionData.endDate)
            
            if (days > 0) {
                const perDayCost = parseFloat(configData.perDayCost)
                const calculatedCost = days * perDayCost
                setSubscriptionData(prev => ({
                    ...prev,
                    totalCost: calculatedCost.toFixed(2)
                }))
            } else {
                setSubscriptionData(prev => ({
                    ...prev,
                    totalCost: ''
                }))
            }
        } else {
            setSubscriptionData(prev => ({
                ...prev,
                totalCost: ''
            }))
        }
    }, [subscriptionData.startDate, subscriptionData.endDate, configData, calculateDays])

    const HandleOnChangeValue = (value, key) => {
        const newData = {
            ...subscriptionData,
            [key]: value
        }

        // Reset end date if start date changes and end date is before new start date
        if (key === 'startDate' && newData.endDate && new Date(newData.endDate) <= new Date(value)) {
            newData.endDate = ''
            newData.totalCost = ''
            setDateError('')
        }

        // Validate date range when end date changes
        if (key === 'endDate' && newData.startDate) {
            const days = calculateDays(newData.startDate, value)
            const minDays = configData ? parseInt(configData.minDays) : 0
            
            if (days < minDays) {
                setDateError(`End date must be at least ${minDays} days after start date (inclusive). Current gap: ${days} day(s)`)
            } else {
                setDateError('')
            }
        }

        setSubscriptionData(newData)
    }

    const onSubmitHandler = async () => {
        // Validation
        if (!subscriptionData.startDate) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "Start Date is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }
        if (!subscriptionData.endDate) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "End Date is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }

        // Validate minimum days gap
        if (configData?.minDays) {
            const days = calculateDays(subscriptionData.startDate, subscriptionData.endDate)
            const minDays = parseInt(configData.minDays)
            if (days < minDays) {
                setToaster({
                    type: "danger",
                    title: "Validation Error",
                    text: `End date must be at least ${minDays} days after start date (inclusive). Current gap: ${days} day(s)`,
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 1500)
                return;
            }
        }

        if (!subscriptionData.totalCost || subscriptionData.totalCost <= 0) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "Total Cost must be greater than 0",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }
        if (!subscriptionData.modeOfPayment) {
            setToaster({
                type: "danger",
                title: "Validation Error",
                text: "Mode of Payment is required",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({ visiblity: "hide" });
            }, 1500)
            return;
        }

        // Convert dates to ISO format
        const payload = {
            startDate: new Date(subscriptionData.startDate).toISOString(),
            endDate: new Date(subscriptionData.endDate + 'T23:59:59.999Z').toISOString(),
            totalCost: parseFloat(subscriptionData.totalCost),
            modeOfPayment: subscriptionData.modeOfPayment,
            isActive: subscriptionData.isActive
        }

        setLoading(true)
        try {
            const response = await services.Vendor.ADD_VENDOR_SUBSCRIPTION(payload);
            console.log(response)
            
            // Store payment data from response
            const responseData = response.data?.data || response.data
            console.log('Subscription response:', responseData)
            
            if (responseData?.subscription && responseData?.razorpayOrder) {
                setPaymentData({
                    subscription: responseData.subscription,
                    razorpayOrder: responseData.razorpayOrder,
                    orderId: responseData.orderId
                })
                
                // Wait a bit to ensure Razorpay script is loaded, then initialize payment
                setTimeout(() => {
                    initializeRazorpayPayment(responseData.razorpayOrder, responseData.subscription.id, responseData)
                }, 500)
            } else {
                setToaster({
                    type: "danger",
                    title: "Error Occured",
                    text: "Invalid response from server",
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 1500)
            }
        } catch (error) {
            console.log(error)
            setToaster({
                type: "danger",
                title: "Error Occured",
                text: error.response?.data?.message || "Failed to add subscription",
                visiblity: "show",
            });
            setTimeout(() => {
                setToaster({
                    visiblity: "hide",
                });
            }, 1500)
        } finally {
            setLoading(false)
        }
    }

    const initializeRazorpayPayment = (razorpayOrder, subscriptionId, responseData = null) => {
        console.log('Initializing Razorpay payment...', { razorpayOrder, subscriptionId })
        console.log('Razorpay available:', !!window.Razorpay)
        
        // Wait for Razorpay to be available
        const checkRazorpay = (attempts = 0) => {
            if (window.Razorpay) {
                console.log('Razorpay is available, proceeding with payment')
                proceedWithPayment()
            } else if (attempts < 5) {
                // Wait a bit more for script to load (max 5 attempts = 5 seconds)
                console.log(`Razorpay not ready, attempt ${attempts + 1}/5`)
                setTimeout(() => {
                    checkRazorpay(attempts + 1)
                }, 1000)
            } else {
                console.error('Razorpay SDK failed to load after multiple attempts')
                setToaster({
                    type: "danger",
                    title: "Error",
                    text: "Razorpay SDK not loaded. Please refresh the page and try again.",
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 3000)
            }
        }

        const proceedWithPayment = () => {
            // Get Razorpay key from constant.js
            const key = constant.REACT_APP_RAZORPAY_KEY || responseData?.razorpayKey || razorpayOrder.key_id
            
            console.log('Razorpay key:', key ? 'Found' : 'Not found')
            
            if (!key) {
                setToaster({
                    type: "danger",
                    title: "Error",
                    text: "Razorpay key not found. Please contact support.",
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 1500)
                return
            }

            if (!razorpayOrder.id) {
                console.error('Razorpay order ID is missing')
                setToaster({
                    type: "danger",
                    title: "Error",
                    text: "Invalid payment order. Please try again.",
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 1500)
                return
            }
            const userDetails = JSON.parse(localStorage.getItem('user_id'))
            const options = {
                key: key,
                amount: razorpayOrder.amount,
                currency: razorpayOrder.currency || 'INR',
                name: "Cars53 Subscription",
                description: "Subscription Payment",
                order_id: razorpayOrder.id,
                handler: async function (response) {
                    console.log('Payment successful:', response)
                    // Payment successful — pass the Razorpay ids so the payment is recorded & verified
                    try {
                        await services.Vendor.ACTIVATE_VENDOR_SUBSCRIPTION(subscriptionId, {
                            razorpayPaymentId: response.razorpay_payment_id,
                            razorpayOrderId: response.razorpay_order_id,
                            razorpaySignature: response.razorpay_signature
                        })
                        setToaster({
                            type: "success",
                            title: "Payment Successful",
                            text: "Subscription activated successfully",
                            visiblity: "show",
                        });
                        setTimeout(() => {
                            navigate('/subscriptions')
                        }, 1500)
                    } catch (error) {
                        console.log(error)
                        setToaster({
                            type: "danger",
                            title: "Error",
                            text: error.response?.data?.message || "Payment successful but failed to activate subscription",
                            visiblity: "show",
                        });
                        setTimeout(() => {
                            setToaster({ visiblity: "hide" });
                        }, 1500)
                    }
                },
                prefill: {
                    name: userDetails.firstName,
                    email: userDetails.email,
                    contact: userDetails.phoneNumber
                },
                notes: {
                    subscription_id: subscriptionId.toString()
                },
                theme: {
                    color: "#3399cc"
                },
                modal: {
                    ondismiss: function() {
                        console.log('Payment modal dismissed')
                        // User closed the payment modal
                        setShowPaymentFailureModal(true)
                    }
                }
            }

            try {
                console.log('Creating Razorpay instance with options:', options)
                const razorpay = new window.Razorpay(options)
                
                razorpay.on('payment.failed', function (response) {
                    // Payment failed
                    console.log('Payment failed:', response)
                    setShowPaymentFailureModal(true)
                })

                // Add error handler for modal opening
                razorpay.on('error', function (error) {
                    console.error('Razorpay error:', error)
                    setToaster({
                        type: "danger",
                        title: "Payment Error",
                        text: error.description || "An error occurred with the payment gateway. Please try again.",
                        visiblity: "show",
                    });
                    setTimeout(() => {
                        setToaster({ visiblity: "hide" });
                    }, 3000)
                })

                console.log('Opening Razorpay payment modal...')
                
                // Ensure body doesn't have overflow hidden that might block the modal
                const originalOverflow = document.body.style.overflow
                document.body.style.overflow = 'auto'
                
                razorpay.open()
                
                // Restore original overflow after a delay (in case modal doesn't open)
                setTimeout(() => {
                    // Check if Razorpay modal is visible
                    const razorpayModal = document.querySelector('.razorpay-container')
                    if (!razorpayModal) {
                        console.warn('Razorpay modal may not have opened')
                    }
                }, 1000)
            } catch (error) {
                console.error('Error initializing Razorpay:', error)
                setToaster({
                    type: "danger",
                    title: "Error",
                    text: "Failed to initialize payment gateway. Please try again.",
                    visiblity: "show",
                });
                setTimeout(() => {
                    setToaster({ visiblity: "hide" });
                }, 1500)
            }
        }

        checkRazorpay()
    }

    const handlePaymentFailureModalClose = () => {
        setShowPaymentFailureModal(false)
        navigate('/subscriptions')
    }

    if (isMobileView) {
        const days = subscriptionData.startDate && subscriptionData.endDate ? calculateDays(subscriptionData.startDate, subscriptionData.endDate) : 0;
        return (
            <>
                <Toaster type={toaster.type} title={toaster.title} text={toaster.text} visiblity={toaster.visiblity} />
                <SideBar />
                <main className="mlux mlux-auth">
                    <MobileHeader back="/subscriptions" />
                    <div className="mlux-greet" style={{ margin: '8px 4px 2px' }} data-rise="1">
                        <div className="kick">Go live with one-way</div>
                        <div className="nm" style={{ fontSize: 22 }}>New Subscription</div>
                    </div>
                    <div className="mlux-card" style={{ padding: 16, marginTop: 12 }} data-rise="2">
                        <label className="mlux-lbl">Start date</label>
                        <div className="mlux-field"><i className="far fa-calendar"></i><input type="date" value={subscriptionData.startDate} min={getTodayDate()} onChange={(e) => HandleOnChangeValue(e.target.value, 'startDate')} /></div>
                        <label className="mlux-lbl">End date</label>
                        <div className="mlux-field"><i className="far fa-calendar-check"></i><input type="date" value={subscriptionData.endDate} min={getMinEndDate()} onChange={(e) => HandleOnChangeValue(e.target.value, 'endDate')} /></div>
                        {dateError && <small style={{ color: '#ef4444', display: 'block', margin: '0 0 8px 4px' }}>{dateError}</small>}
                        <label className="mlux-lbl">Payment mode</label>
                        <div className="mlux-field no-icon select"><select value={subscriptionData.modeOfPayment} onChange={(e) => HandleOnChangeValue(e.target.value, 'modeOfPayment')}><option value="online">Online</option></select></div>
                    </div>

                    <div className="mlux-card mlux-fare" style={{ marginTop: 16 }} data-rise="3">
                        {configData?.perDayCost && <div className="r"><span>Per day</span><b>₹{configData.perDayCost}</b></div>}
                        {days > 0 && <div className="r"><span>Duration</span><b>{days} day{days === 1 ? '' : 's'}</b></div>}
                        <div className="r tot"><span className="lbl">Total</span><span className="amt">₹{subscriptionData.totalCost || '—'}</span></div>
                    </div>

                    <button className="mlux-btn" style={{ marginTop: 16 }} disabled={loading || !subscriptionData.totalCost} onClick={onSubmitHandler}><i className="far fa-circle-check"></i> {loading ? 'Processing…' : 'Subscribe & pay'}</button>
                </main>
            </>
        );
    }

    return (<>
        <Header />
        <SideBar />
        <main class="main">

            <Toaster
                type={toaster.type}
                title={toaster.title}
                text={toaster.text}
                visiblity={toaster.visiblity}
            />

            <div class="user-profile py-5">
                <div class="container">
                    <div class="row">
                        <DashboardSideBar />
                        <div class="col-lg-9">
                            <div class="user-profile-wrapper">
                                <div class="row">
                                    <div class="col-lg-12">
                                        <div class="user-profile-card">
                                            <div class="user-profile-card-header">
                                                <h4 class="user-profile-card-title">Add Subscription</h4>
                                            </div>
                                            <div class="user-profile-form">

                                                <div class="row">
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Start Date <span class="text-danger">*</span></label>
                                                            <input
                                                                type="date"
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'startDate')}
                                                                class="form-control"
                                                                value={subscriptionData.startDate}
                                                                placeholder="Start Date"
                                                                min={getTodayDate()}
                                                                required
                                                                disabled={configLoading}
                                                            />
                                                            {!subscriptionData.startDate && <small className="text-danger">Start Date is required</small>}
                                                            {configData?.minDays && <small className="text-muted">Minimum subscription period: {configData.minDays} days</small>}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>End Date <span class="text-danger">*</span></label>
                                                            <input
                                                                type="date"
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'endDate')}
                                                                class="form-control"
                                                                value={subscriptionData.endDate}
                                                                placeholder="End Date"
                                                                min={getMinEndDate()}
                                                                disabled={!subscriptionData.startDate || configLoading}
                                                                required
                                                            />
                                                            {!subscriptionData.endDate && <small className="text-danger">End Date is required</small>}
                                                            {dateError && <small className="text-danger">{dateError}</small>}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Total Cost (Rs.) <span class="text-danger">*</span></label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                class="form-control"
                                                                value={subscriptionData.totalCost}
                                                                placeholder="Total Cost"
                                                                readOnly
                                                                disabled={configLoading}
                                                            />
                                                            {configData?.perDayCost && (
                                                                <small className="text-muted">
                                                                    Cost per day: Rs. {parseFloat(configData.perDayCost).toFixed(2)}
                                                                    {subscriptionData.startDate && subscriptionData.endDate && (
                                                                        <span> × {calculateDays(subscriptionData.startDate, subscriptionData.endDate)} days</span>
                                                                    )}
                                                                </small>
                                                            )}
                                                            {(!subscriptionData.totalCost || subscriptionData.totalCost <= 0) && <small className="text-danger">Please select valid start and end dates</small>}
                                                        </div>
                                                    </div>
                                                    <div class="col-md-6">
                                                        <div class="form-group">
                                                            <label>Mode of Payment <span class="text-danger">*</span></label>
                                                            <select
                                                                onChange={(e) => HandleOnChangeValue(e.target.value, 'modeOfPayment')}
                                                                class="form-control"
                                                                value={subscriptionData.modeOfPayment}
                                                                disabled={true}
                                                                required
                                                            >
                                                                <option value="">Select Payment Mode</option>
                                                               
                                                                <option value="online">Online</option>
                                                            </select>
                                                            {!subscriptionData.modeOfPayment && <small className="text-danger">Mode of Payment is required</small>}
                                                        </div>
                                                    </div>
                                                        {/* <div class="col-md-6">
                                                            <div class="form-group">
                                                                <label>Status</label>
                                                                <select
                                                                    onChange={(e) => HandleOnChangeValue(e.target.value === 'true', 'isActive')}
                                                                    class="form-control"
                                                                    value={subscriptionData.isActive ? 'true' : 'false'}
                                                                >
                                                                    <option value="true">Active</option>
                                                                    <option value="false">Inactive</option>
                                                                </select>
                                                            </div>
                                                        </div> */}
                                                </div>
                                                <div class="d-flex align-items-center gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={onSubmitHandler}
                                                        class="theme-btn my-3"
                                                        disabled={loading}
                                                    >
                                                        <span class="far fa-save"></span> {loading ? 'Saving...' : 'Save Subscription'}
                                                    </button>
                                                    <Link to={'/subscriptions'} class="theme-btn my-3" style={{ background: '#6c757d' }}>
                                                        <span class="far fa-times"></span> Cancel
                                                    </Link>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </main>
        <Footer />

        {/* Payment Failure Modal */}
        {showPaymentFailureModal && (
            <div 
                className="modal fade show" 
                style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} 
                tabIndex="-1" 
                role="dialog"
                onClick={handlePaymentFailureModalClose}
            >
                <div 
                    className="modal-dialog modal-dialog-centered" 
                    role="document"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Payment Failed</h5>
                            <button type="button" className="btn-close" onClick={handlePaymentFailureModalClose} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <p>Your payment could not be processed. Please try again or contact support if the problem persists.</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn theme-btn" onClick={handlePaymentFailureModalClose}>Go to Subscriptions</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
    </>
    )
}
export default AddSubscription

